/**
 * Hook providing welcome screen content for new conversations.
 * Returns concierge defaults for the default agency, or fetched agency data otherwise.
 */
import { DEFAULT_AGENCY_SLUG } from '@saaga/shared/constants/agency.constants'
import { useGetAgencyAnyQuery } from '@saaga/shared/store/agency-endpoints.store'
import { PlusCircleIcon, StoreIcon } from 'lucide-react'
import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import type { ChatWelcomeQuickAction } from '@/components/chat/chat-welcome-screen.interface'
import { useOrgSlug } from '@/hooks/use-org-slug.hook'
import { agencyRoutes, orgRoutes } from '@/routes/org-routes.const'

import conciergeDescription from '../../components/chat/chat-welcome-screen.docs.md?raw'

const CONCIERGE_TITLE = 'Welcome! What would you like to research today?'
const CONCIERGE_CHAT_HINT = 'Or just chat! 🤓'

interface UseChatWelcomeReturn {
  title: string
  description: string
  quickActionList?: ChatWelcomeQuickAction[]
  chatHint?: string
  isLogoVisible: boolean
}

const EMPTY_WELCOME: UseChatWelcomeReturn = {
  title: '',
  description: '',
  isLogoVisible: false,
}

export const useChatWelcome = (
  agencySlug: string | null,
  isEnabled: boolean
): UseChatWelcomeReturn => {
  const navigate = useNavigate()
  const orgSlug = useOrgSlug()

  const isDefaultAgency = !agencySlug || agencySlug === DEFAULT_AGENCY_SLUG
  const shouldFetchAgency = isEnabled && !isDefaultAgency && !!agencySlug

  const { data: agencyResponse } = useGetAgencyAnyQuery(agencySlug ?? '', {
    skip: !shouldFetchAgency,
  })

  const handleBrowseMarketplace = useCallback(() => {
    navigate(`/${orgSlug}/${orgRoutes.agencies}/${agencyRoutes.explore}`)
  }, [navigate, orgSlug])

  const handleCreateAgency = useCallback(() => {
    navigate(`/${orgSlug}/${orgRoutes.agencies}/${agencyRoutes.studio}/${agencyRoutes.new}`)
  }, [navigate, orgSlug])

  const conciergeQuickActionList: ChatWelcomeQuickAction[] = useMemo(
    () => [
      { icon: StoreIcon, label: 'Browse Marketplace', onClick: handleBrowseMarketplace },
      { icon: PlusCircleIcon, label: 'Create Your Agency', onClick: handleCreateAgency },
    ],
    [handleBrowseMarketplace, handleCreateAgency]
  )

  return useMemo(() => {
    if (!isEnabled) return EMPTY_WELCOME

    if (isDefaultAgency) {
      return {
        title: CONCIERGE_TITLE,
        description: conciergeDescription,
        quickActionList: conciergeQuickActionList,
        chatHint: CONCIERGE_CHAT_HINT,
        isLogoVisible: true,
      }
    }

    if (!agencyResponse) {
      return EMPTY_WELCOME
    }

    return {
      title: agencyResponse.presentation_name,
      description: agencyResponse.presentation_description_short ?? '',
      isLogoVisible: false,
    }
  }, [isEnabled, isDefaultAgency, agencyResponse, conciergeQuickActionList])
}
